<template>
  <div>
    <add-edit-task-dialog
      v-if="showAddTaskDialog"
      :add-or-edit-task="newTask"
      @accept="showAddTaskDialog = false"
      @cancel="showAddTaskDialog = false"
    />
    <v-menu offset-y left>
      <template #activator="{ on }">
        <v-btn icon small outlined v-on="on">
          <bm-icon icon="Add" size="20px" />
        </v-btn>
      </template>

      <v-list dense>
        <v-list-item
          v-for="(item, index) in quickActions"
          :key="index"
          @click="item.action"
        >
          {{ item.name }}
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script setup lang="ts">
import { routes } from "@/app/ui/navigation/menuItems";
import { useI18n } from "@/plugins/i18n";
import { useRouter } from "@/router/router";
import { ref } from "vue";
import { makeDeepRef } from "@/components/composables/componentTools";
import AddEditTaskDialog from "@/components/pages/tasks/subcomponents/AddEditTaskDialog.vue";
import { BmTask } from "@/app/services/generated/bmtaskService";
import { showModalDialog } from "@/components/platform/common/showModalDialog";
import LogCommunicationDialog from "@/components/pages/outlet/crm/communication_logs/LogCommunicationDialog.vue";
import type { Route } from "vue-router";
import {
  showFillContainersDialog,
  updateContainers,
} from "@/components/pages/containers/composables/fillContainers";
import { showCreateSalesVisitDialog } from "@/components/pages/orders/salesvisits/salesVisitDialogUtils";
import PrintLabelsDialog from "@/components/pages/production/assemblies/subcomponents/PrintLabelsDialog.vue";
import { salesRoutes } from "@/app/ui/navigation/salesMenu";
import { addVisitWithDialog } from "@/business_logic/distribution/distributionVisitUtils";

interface QuickAction {
  name: string;
  action: () => Promise<Route | void> | void;
}

const { router } = useRouter();
const { $m } = useI18n();
const newTask = makeDeepRef(new BmTask());
const showAddTaskDialog = ref(false);

const quickActions: QuickAction[] = [
  {
    name: $m.quick_action_new_task,
    action: () => {
      showAddTaskDialog.value = true;
    },
  },
  {
    name: $m.quick_action_new_credit,
    action: async () => await router.push(routes.salesMenu.createCreditNote()),
  },
  {
    name: $m.quick_action_new_order,
    action: async () => await router.push(salesRoutes.createOrder.location({})),
  },
  {
    name: $m.quick_action_new_visit,
    action: async () => {
      await addVisitWithDialog({});
    },
  },
  {
    name: $m.quick_action_new_sales_visit,
    action: async () => {
      await showCreateSalesVisitDialog({
        entityType: "outlet",
      });
    },
  },
  {
    name: $m.quick_action_new_outlet,
    action: async () =>
      await router.push(routes.contactsMenu.outlets.createLocation()),
  },
  {
    name: $m.quick_action_new_stock_transfer,
    action: async () =>
      await router.push(
        routes.stockMenu.stockMovements.stockTransfer.createStockTransfer()
      ),
  },
  {
    name: $m.quick_action_new_stock_adjustment,
    action: async () =>
      await router.push(
        routes.stockMenu.stockMovements.stockAdjustment.createLocation()
      ),
  },
  {
    name: $m.quick_action_new_purchase_order,
    action: async () =>
      await router.push({
        name: routes.stockMenu.purchaseOrdering.create,
      }),
  },
  {
    name: $m.crm_log_communication,
    action: async () => {
      await showModalDialog<void, typeof LogCommunicationDialog>(
        LogCommunicationDialog,
        { props: {} }
      );
    },
  },
  {
    name: $m.fill_containers_text,
    action: async () => {
      const result = await showFillContainersDialog({});
      if (result.wasSuccessful && result.value) {
        await updateContainers(result.value);
      }
    },
  },
  {
    name: $m.stock_print_labels,
    action: async () => {
      await showModalDialog<void, typeof PrintLabelsDialog>(PrintLabelsDialog, {
        props: {},
      });
    },
  },
];
</script>
